/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { message, Space } from 'antd';
import { Formik } from 'formik';
import { get } from 'lodash';
import { omit, pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ApplicationMarketTypeFragmentFragment } from '../../../../api/fragments/ApplicationMarketTypeFragment';
import { type StandardApplicationModuleSpecsFragment } from '../../../../api/fragments/StandardApplicationModuleSpecs';
import {
    UpdateStandardApplicationModuleMainDetailsDocument,
    type UpdateStandardApplicationModuleMainDetailsMutation,
    type UpdateStandardApplicationModuleMainDetailsMutationVariables,
} from '../../../../api/mutations/updateStandardApplicationModuleMainDetails';
import {
    ApplicationMarket,
    type ApplicationModuleMarketTypeInput,
    EventLeadOriginType,
    EventMediumType,
    type StandardApplicationModuleUpdateSettings,
} from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import { hasValue } from '../../../../utilities/fp';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import { hasAppointmentScenario, hasPaymentScenario } from '../modules/implementations/shared';
import CounterSettingDetails from '../shared/CounterSettingDetails';
import { newZealandMarketValidator, singaporeMarketValidator } from '../shared/validators';
import StandardApplicationModuleMainDetailsForm from './StandardApplicationModuleMainDetailsForm';
import { type EditMode, type StandardApplicationCounterFieldKey } from './type';

const counterFields: StandardApplicationCounterFieldKey[] = [
    'leadCounter',
    'applicationCounter',
    'reservationCounter',
    'appointmentCounter',
    'insuranceCounter',
];

export type FormValues = StandardApplicationModuleUpdateSettings &
    Pick<StandardApplicationModuleSpecsFragment, StandardApplicationCounterFieldKey>;

export type MainDetailsProps = {
    module: StandardApplicationModuleSpecsFragment;
    editMode: EditMode;
    setEditMode: (value: EditMode) => void;
};

export const getFinancingApplicationMarket = (
    marketType: ApplicationMarketTypeFragmentFragment
): ApplicationModuleMarketTypeInput => {
    switch (marketType.__typename) {
        case 'SingaporeApplicationMarket':
            return { singapore: { coe: marketType.coe } };

        case 'NewZealandApplicationMarket':
            return {
                newZealand: {
                    ppsr: marketType.ppsr,
                    estFee: marketType.estFee,
                    bankEstFee: marketType.bankEstFee,
                    nzFees: marketType.nzFees,
                },
            };

        default:
            return null;
    }
};

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.only(
        values => hasAppointmentScenario(values?.scenarios),
        validators.requiredString('appointmentModuleId')
    ),
    validators.only(
        values => hasPaymentScenario(values?.scenarios),
        validators.compose(
            validators.requiredString('paymentSettingsId'),
            validators.requiredNumber('depositAmount.defaultValue')
        )
    ),
    validators.requiredArray('scenarios'),
    validators.only(values => hasValue('bankModuleId')(values), validators.requiredString('market')),
    validators.only(values => {
        const showInsuranceCalculator = get('showInsuranceCalculator', values);

        return showInsuranceCalculator === true;
    }, validators.requiredString('insuranceModuleId')),
    validators.only(values => {
        const showFinanceCalculator = get('showFinanceCalculator', values);

        return showFinanceCalculator;
    }, validators.requiredString('bankModuleId')),
    validators.only(values => {
        const market = get('market', values);
        const marketType = get('marketType', values);

        return market === ApplicationMarket.Singapore && !!marketType;
    }, singaporeMarketValidator),
    validators.only(values => {
        const market = get('market', values);
        const marketType = get('marketType', values);

        return market === ApplicationMarket.NewZealand && !!marketType;
    }, newZealandMarketValidator),
    validators.requiredBoolean('isOcrEnabled'),
    validators.requiredBoolean('tradeIn'),
    validators.requiredBoolean('showResetKYCButton'),
    validators.only(
        ({ capModuleId }) => capModuleId,
        validators.compose(
            validators.requiredBoolean('capPrequalification'),
            validators.requiredStringEnum('leadOrigin', EventLeadOriginType),
            validators.requiredStringEnum('leadMedium', EventMediumType),
            validators.only(
                ({ leadMedium }) => leadMedium !== EventMediumType.Walkin,
                validators.requiredString('leadCampaignId')
            )
        )
    )
);

const StandardApplicationModuleMainDetails = ({ module, editMode, setEditMode }: MainDetailsProps) => {
    const { t } = useTranslation(['moduleDetails']);

    const validate = useValidator(validator);

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(
                [
                    'displayName',
                    'market',
                    'depositAmount',
                    'isOcrEnabled',
                    'scenarios',
                    'promoCodeModuleId',
                    'showResetKYCButton',
                    'myInfoSettingId',
                    'showRemoteFlowButtonInKYCPage',
                    'skipForDeposit',
                    'showFromValueOnVehicleDetails',
                    'bankDisplayPreference',
                    'bankDisplayInSharePdf',
                    'tradeIn',
                    'isTradeInAmountVisible',
                    'testDrive',
                    'displayAppointmentDatepicker',
                    'appointmentModuleId',
                    'insurerDisplayPreference',
                    'insurerIds',
                    'isFinancingOptional',
                    'isInsuranceOptional',
                    'showFinanceCalculator',
                    'showInsuranceCalculator',
                    'paymentSettingsId',
                    'hasDealerOptions',
                    'includeDealerOptionsForFinancing',
                    'flexibleDiscount',
                    'bankIds',
                    'capModuleId',
                    'isSearchCapCustomerOptional',
                    'capPrequalification',
                    'leadOrigin',
                    'leadMedium',
                    'leadCampaignId',
                    ...counterFields,
                ],
                module
            ),
            priceDisclaimer: module.dealerPriceDisclaimer,
            marketType: getFinancingApplicationMarket(module.marketType),
            bankModuleId: module?.bankModule?.id,
        }),
        [module]
    );

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<FormValues>(
        async values => {
            // submitting message
            message.loading({
                content: t('standardApplicationModuleDetails:messages.updateMainDetailsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            await apolloClient
                .mutate<
                    UpdateStandardApplicationModuleMainDetailsMutation,
                    UpdateStandardApplicationModuleMainDetailsMutationVariables
                >({
                    mutation: UpdateStandardApplicationModuleMainDetailsDocument,
                    variables: {
                        moduleId: module.id,
                        settings: {
                            ...omit(counterFields, values),
                            testDrive: hasAppointmentScenario(values.scenarios),
                            bankModuleId: values.showFinanceCalculator ? values.bankModuleId : null,
                            capModuleId: values.capModuleId || null,
                        },
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('standardApplicationModuleDetails:messages.updateMainDetailsSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateMainDetails" name="updateMainDetails" onSubmitCapture={handleSubmit}>
                    <Space direction="vertical">
                        <StandardApplicationModuleMainDetailsForm module={module} />
                        <CounterSettingDetails names={counterFields} />
                    </Space>
                </Form>
            )}
        </Formik>
    );
};

export default StandardApplicationModuleMainDetails;
